import {
  Button,
  Dialog,
  DialogTitle,
  Paper,
  TableSortLabel,
} from "@mui/material";
import { Stack } from "@mui/system";
import { Link as RouterLink } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { formatDateTime } from "../../../utilitiesOfBrandConfig/helperOfBrandConfig";
import dayjs from "dayjs";
import StatusChip from "../../../utilitiesOfBrandConfig/StatusChip";

interface KeywordsLastRunsProps {
  open: boolean;
  handleClose: () => void;
  tableHeadings: { [key: string]: string };
}

const tableHeaders = [
  {
    id: 1,
    KEY: "process_name",
    label: "Process Name",
    sortable: true,
  },
  {
    id: 2,
    KEY: "range",
    label: "Range",
    sortable: true,
  },
  { id: 3, KEY: "status", label: "Status", sortable: true },
  { id: 6, KEY: "step", label: "Step", sortable: true },
  { id: 4, KEY: "started_at", label: "Run Start", sortable: true },
  { id: 5, KEY: "ended_at", label: "Run End", sortable: true },
];

const LastRuns = ({
  open,
  handleClose,
  tableHeadings,
}: KeywordsLastRunsProps) => {
  const [sortedData, setSortedData] = useState<any[]>([]);
  const [sort, setSort] = useState<{
    keytoSort: string;
    direction: "asc" | "desc";
  }>({ keytoSort: "started_at", direction: "desc" });

  const brandOverviewLastRunData = useSelector(
    (state: any) => state?.brandConfigurationReport?.brandOverviewLastRunData,
  );

  const headCells = Object.entries(tableHeadings);
  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      // If no sort is applied, return original order
      if (!sort.keytoSort || !sort.direction) {
        return [...arrayToSort]; // Return a copy of original order
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined || keytoSort === null) return 0;

        let aValue;
        let bValue;

        if (keytoSort === "range") {
          aValue = a.run_from ? dayjs(a.run_from).valueOf() : 0;

          bValue = b.run_from ? dayjs(b.run_from).valueOf() : 0;
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;
        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort],
  );

  useEffect(() => {
    if (!brandOverviewLastRunData || brandOverviewLastRunData.length === 0)
      return;

    // Then sort the data with accurate counts
    const sortedRows = getSortedData(brandOverviewLastRunData || []);

    setSortedData(sortedRows);
  }, [brandOverviewLastRunData, getSortedData]);

  // close the modal and reset the sorts
  const handleCloseAndReset = () => {
    setSort({ keytoSort: "started_at", direction: "desc" });
    handleClose();
  };

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    setSort({
      keytoSort: header.KEY,
      // Toggle sort direction if the same column is clicked, otherwise default to ascending
      direction:
        header.KEY === sort.keytoSort && sort.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseAndReset}
      PaperProps={{ sx: { width: "100%", maxWidth: "1200px" } }}
    >
      <Paper sx={{ padding: "16px 24px 20px 24px" }}>
        <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
          Last 10 Runs
        </DialogTitle>

        <TableContainer
          className="KeywordsTableWrap"
          component={Paper}
          sx={{ boxShadow: "none", maxHeight: 640 }}
        >
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header) => (
                  <TableCell key={header.id} align="left">
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={
                        sort.keytoSort === header.KEY ? sort.direction : "asc"
                      }
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData?.length > 0 ? (
                sortedData?.map((row) => (
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell
                        key={cellIndex}
                        align="left"
                        sx={{ py: "3px" }}
                      >
                        {key === "process_name" ? (
                          <>
                            <RouterLink
                              to={row?.link ? row.link : "#"}
                              style={{
                                textDecoration: "underline",
                                color: "#9C27B0",
                              }}
                              target="_blank"
                            >
                              {row[key] ? row[key] : ""}
                            </RouterLink>
                          </>
                        ) : key === "status" ? (
                          <StatusChip status={row[key]} />
                        ) : key === "step" ? (
                          <>
                            {" "}
                            {row.status !== "Succeeded" &&
                            row.status !== "Running"
                              ? row[key]
                              : ""}
                          </>
                        ) : key === "range" ? (
                          <>
                            {row.run_from
                              ? dayjs(row.run_from).format("MMM. DD, YYYY")
                              : ""}
                            {" ➜ "}
                            {row.run_to
                              ? dayjs(row.run_to).format("MMM. DD, YYYY")
                              : ""}
                          </>
                        ) : key === "started_at" ? (
                          <> {row[key] ? formatDateTime(row[key]) : ""}</>
                        ) : key === "ended_at" ? (
                          <> {row[key] ? formatDateTime(row[key]) : ""}</>
                        ) : (
                          ""
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCloseAndReset}
          >
            Close
          </Button>
        </Stack>
      </Paper>
    </Dialog>
  );
};

export default LastRuns;
